import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import type { AdjustHandle, AdjustRange } from '@src/hooks/useOverlayRangeAdjust';

export interface OverlayRangeHandlesProps {
  /** Effective range to position against (preview while dragging, else committed). */
  range: AdjustRange;
  /** Resolve the local scroll container the word spans live in. */
  getScrollContainer: () => HTMLElement | null | undefined;
  /** DOM attribute carrying the word index (always `data-index` here). */
  spanIndexAttr: string;
  /** The handle currently being dragged (lit gold), or null. */
  activeHandle: AdjustHandle | null;
  /** Pointer-down on a handle starts a drag. */
  onBeginDrag: (handle: AdjustHandle, e: React.PointerEvent) => void;
  /** Arrow-key nudge of a boundary. */
  onNudge: (handle: AdjustHandle, direction: -1 | 1) => void;
  /** Escape / dismiss adjust mode. */
  onExit: () => void;
}

type HandlePos = { left: number; top: number; height: number };

/** Matches `text-underline-offset: 3px` in overlayHighlightStyles. */
const UNDERLINE_OFFSET = 3;
/** Side length of the square handle. */
const HANDLE_SIZE = 9;

/**
 * Draggable left/right handles on an overlay's transcript underline. Lets the
 * user re-anchor which words the overlay starts/ends on (PLAT — overlay range
 * handles). Portals to `document.body` with `position: fixed`: the Content
 * Studio clip preview mounts the transcript inside a modal whose `transform`
 * ancestor breaks in-container fixed positioning and stacking (the same reason
 * the hover pill portals). Each handle is positioned independently against its
 * own boundary span's rect, so a range spanning multiple visual lines just
 * works.
 */
export const OverlayRangeHandles: React.FC<OverlayRangeHandlesProps> = ({
  range,
  getScrollContainer,
  spanIndexAttr,
  activeHandle,
  onBeginDrag,
  onNudge,
  onExit,
}) => {
  const [startPos, setStartPos] = useState<HandlePos | null>(null);
  const [endPos, setEndPos] = useState<HandlePos | null>(null);
  const rafRef = useRef<number | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  const recompute = useCallback(() => {
    const container = getScrollContainer();
    if (!container) {
      setStartPos(null);
      setEndPos(null);
      return;
    }
    const startEl = container.querySelector<HTMLElement>(
      `[${spanIndexAttr}='${range.start}']`,
    );
    const endEl = container.querySelector<HTMLElement>(
      `[${spanIndexAttr}='${range.end}']`,
    );
    const containerRect = container.getBoundingClientRect();
    // Clip handles to the container's visible band so they don't float over
    // the header / outside the scroll viewport.
    const within = (r: DOMRect) =>
      r.bottom > containerRect.top && r.top < containerRect.bottom;

    // The text-decoration underline sits at the baseline + offset, which is
    // ABOVE the line-box bottom (`rect.bottom`) by the line-height padding.
    // Recover the underline's Y from the font metrics so the handle centers on
    // the visible blue line, not in the gap below it. Centering the handle's
    // own height on that Y lines it up in the middle of the underline.
    const underlineCenterY = (el: HTMLElement, rect: DOMRect): number => {
      const fontSize = parseFloat(getComputedStyle(el).fontSize) || 16;
      // The underline renders just below the text's em box. Anchor to the em
      // box bottom + the underline offset so the circle centers on the line.
      const emBoxBottom = rect.bottom - (rect.height - fontSize) / 2;
      return emBoxBottom + UNDERLINE_OFFSET;
    };

    const startR = startEl?.getBoundingClientRect() ?? null;
    const endR = endEl?.getBoundingClientRect() ?? null;
    // Start handle caps the LEFT edge of the first word; end handle caps the
    // RIGHT edge of the last word — i.e. the two ends of the underline itself,
    // so both circles stay attached to the line.
    setStartPos(
      startEl && startR && within(startR)
        ? {
            left: startR.left,
            top: underlineCenterY(startEl, startR) - HANDLE_SIZE / 2,
            height: HANDLE_SIZE,
          }
        : null,
    );
    setEndPos(
      endEl && endR && within(endR)
        ? {
            left: endR.right,
            top: underlineCenterY(endEl, endR) - HANDLE_SIZE / 2,
            height: HANDLE_SIZE,
          }
        : null,
    );
  }, [getScrollContainer, spanIndexAttr, range.start, range.end]);

  const scheduleRecompute = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      recompute();
    });
  }, [recompute]);

  // Reposition on range change + scroll / resize / container reflow.
  useEffect(() => {
    recompute();
    const container = getScrollContainer();
    window.addEventListener('resize', scheduleRecompute);
    // Capture-phase: catch scrolls on the container or any nested scroller.
    window.addEventListener('scroll', scheduleRecompute, true);
    let ro: ResizeObserver | null = null;
    if (container && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(scheduleRecompute);
      ro.observe(container);
    }
    return () => {
      window.removeEventListener('resize', scheduleRecompute);
      window.removeEventListener('scroll', scheduleRecompute, true);
      ro?.disconnect();
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [recompute, scheduleRecompute, getScrollContainer]);

  // Escape exits adjust mode.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExit();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onExit]);

  // Clicking anywhere outside a handle exits adjust mode. Capture-phase so it
  // fires regardless of downstream stopPropagation; pointers landing on a
  // handle (a DOM child of the layer) are ignored so a drag isn't dismissed.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (target && layerRef.current?.contains(target)) return;
      onExit();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, [onExit]);

  const renderHandle = (handle: AdjustHandle, pos: HandlePos | null) => {
    // The handle is GLUED to the snapped boundary — the actual end of the
    // underline (start handle: left edge of the first word; end handle: right
    // edge of the last word). As the drag re-snaps the range word-by-word, the
    // boundary span changes, `recompute` repositions, and the circle jumps with
    // the line so the two never separate. It never follows the raw cursor into
    // the gaps between words/lines (which would detach it from the underline).
    // If the boundary span is scrolled out of view, hide the handle rather than
    // float it; it reappears when scrolled back.
    if (!pos) return null;
    const isActive = activeHandle === handle;
    const left = pos.left;
    const top = pos.top;
    return (
      <Handle
        type="button"
        role="slider"
        $active={isActive}
        aria-label={
          handle === 'start' ? 'Adjust overlay start' : 'Adjust overlay end'
        }
        aria-valuenow={handle === 'start' ? range.start : range.end}
        style={{ left: `${left}px`, top: `${top}px`, height: `${HANDLE_SIZE}px` }}
        onPointerDown={e => onBeginDrag(handle, e)}
        onKeyDown={e => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            onNudge(handle, -1);
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            onNudge(handle, 1);
          }
        }}
        data-testid={`overlay-range-handle-${handle}`}
      />
    );
  };

  return createPortal(
    <Layer ref={layerRef} data-testid="overlay-range-handles">
      {renderHandle('start', startPos)}
      {renderHandle('end', endPos)}
    </Layer>,
    document.body,
  );
};

const Layer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  /* Seven-figure scale so the handles clear the clip-preview modal's stacking
     context, but BELOW the hover pill + quick-edit popover so those paint on
     top. (Six-figure values sit behind the modal and vanish.) */
  z-index: ${CLIP_PREVIEW_Z.overlayRangeHandles};
`;

const Handle = styled.button<{ $active: boolean }>`
  /* Small circle at each end of the overlay underline. Same blue as the
     underline at rest; lit gold while being dragged. No outline. */
  position: fixed;
  width: ${HANDLE_SIZE}px;
  transform: translateX(-50%);
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${p =>
    p.$active
      ? 'var(--accent, #f4c542)'
      : 'var(--content-overlays-hover, #60a5fa)'};
  cursor: ew-resize;
  pointer-events: auto;
  touch-action: none;

  /* Hover → yellow; a handle mid-drag stays gold even while hovered. */
  &:hover {
    background: ${p => (p.$active ? 'var(--accent, #f4c542)' : '#facc15')};
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;
